import { useMutation } from "@tanstack/react-query";
import { apiDeleteUser, apiSuspendUserAccess } from "../../api/access";
import { useTheme } from "@mui/material/styles";
import { useNotificationService } from "../notification";
import { useAuth } from "../../hooks/useAuth";
import ModalContainer from "../UIElements/modal-container";
import { Box, Button, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { getUrlPathId } from "../../utils/url-id";
import { useLocation, useNavigate, useNavigation } from "react-router-dom";
import { PATH_USER } from "../../routes/path";

export const SuspendUserAccount = ({
  open,
  modalHandler,
}: {
  open: boolean;
  modalHandler: () => void;
}) => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const navigate = useNavigate();
  const location = useLocation();

  const mutation = useMutation({
    mutationFn: (field: { userId: string; suspend: boolean }) =>
      apiSuspendUserAccess(field),
    onSuccess: (data) => {
      if (data?.status === "error") {
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Success");
        navigate(PATH_USER.root);
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });

  const handleSuspendUser = () => {
    const userId = getUrlPathId(location?.pathname);
    mutation.mutate({
      userId: userId,

      suspend: true,
    });
  };

  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"xs"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 3, borderRadius: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            color: "#292925",
          }}
        >
          Suspend Users
        </Typography>

        <Typography variant="body1" color={"black"} sx={{ my: 2 }}>
          Are you sure you want to suspend this user
        </Typography>

        <Stack
          direction={"row"}
          justifyContent={"space-around"}
          sx={{ gap: 3 }}
        >
          <Button
            onClick={modalHandler}
            sx={{
              width: 150,
              fontWeight: 700,
              borderRadius: "12px",
              background: theme.palette.lightBlue.light,
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleSuspendUser}
            color="warning"
            loading={mutation.isPending}
            sx={{
              width: 170,
              borderRadius: "12px",
              fontWeight: 700,
              color: "white",
            }}
            variant="contained"
          >
            Suspend
          </LoadingButton>
        </Stack>
      </Box>
    </ModalContainer>
  );
};

export const DeleteUserAccount = ({
  open,
  modalHandler,
}: {
  open: boolean;
  modalHandler: () => void;
}) => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const location = useLocation();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (userId: string) => apiDeleteUser(userId),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg || "", "error");
          navigate(PATH_USER.root);
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Success");
        navigate(PATH_USER.root);
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });

  const handleDeleteUser = () => {
    const userId = getUrlPathId(location?.pathname);
    mutation.mutate(userId);
  };

  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"xs"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 3, borderRadius: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            color: "#292925",
          }}
        >
          Suspend Users
        </Typography>

        <Typography variant="body1" color={"black"} sx={{ my: 2 }}>
          Are you sure you want to delete this user
        </Typography>

        <Stack
          direction={"row"}
          justifyContent={"space-around"}
          sx={{ gap: 3 }}
        >
          <Button
            onClick={modalHandler}
            sx={{
              width: 150,
              fontWeight: 700,
              borderRadius: "12px",
              background: theme.palette.lightBlue.light,
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleDeleteUser}
            color="error"
            loading={mutation.isPending}
            sx={{
              width: 170,
              borderRadius: "12px",
              fontWeight: 700,

              color: "white",
            }}
            variant="contained"
          >
            Delete
          </LoadingButton>
        </Stack>
      </Box>
    </ModalContainer>
  );
};
